// ============================================
// FILE: frontend/src/pages/admin/AdminConnectionsPage.jsx
// PURPOSE: Admin page to view and manage all connections
// FIXED: Added handleStatusChange function and status update logic
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../hooks/useNotification';
import { useConnections } from '../../hooks/useConnections';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription
} from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Loader2, AlertCircle, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';

export default function AdminConnectionsPage() {
  console.log('🎨 [AdminConnectionsPage] Page mounted');

  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const {
    connections,
    loading,
    updatingId,
    error,
    pagination,
    fetchAllConnections,
    updateStatus
  } = useConnections();

  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Fetch connections on filter/sort/page changes
  useEffect(() => {
    console.log('[AdminConnectionsPage] Fetching connections...', {
      page: currentPage,
      status: statusFilter,
      sort: sortBy
    });
    fetchAllConnections(currentPage, 20, statusFilter, sortBy);
  }, [currentPage, statusFilter, sortBy, fetchAllConnections]);

  // Handle status update with admin notes
  const handleStatusChange = async (connectionId, newStatus) => {
    console.log('[AdminConnectionsPage] Changing status for:', {
      connectionId,
      newStatus
    });

    // Prompt for optional admin notes
    const adminNotes = prompt('Add notes about this status change (optional):');

    if (adminNotes === null) {
      console.log('[AdminConnectionsPage] Status change cancelled');
      return;
    }

    try {
      console.log('[AdminConnectionsPage] 📤 Calling updateStatus API...');
      await updateStatus(connectionId, newStatus, adminNotes || '');
      
      console.log('[AdminConnectionsPage] ✅ Status updated successfully');
      showSuccess('Success', `Status updated to ${newStatus.replace(/_/g, ' ')}`);
      
      // Refresh the connections list
      console.log('[AdminConnectionsPage] Refreshing connections list');
      fetchAllConnections(currentPage, 20, statusFilter, sortBy);
    } catch (err) {
      console.error('[AdminConnectionsPage] ❌ Update failed:', err);
      showError('Failed', err.message || 'Failed to update connection status');
    }
  };

  const handleViewChat = (connectionId) => {
    console.log('[AdminConnectionsPage] Navigating to chat:', connectionId);
    navigate(`/connections/${connectionId}/chat`);
  };

  // Helper to get badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'both_contacted':
        return 'bg-blue-100 text-blue-800';
      case 'deal_in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'deal_completed':
        return 'bg-emerald-100 text-emerald-800';
      case 'deal_failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Loading state
  if (loading && connections.length === 0) {
    console.log('[AdminConnectionsPage] Loading connections');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading connections...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && connections.length === 0) {
    console.error('[AdminConnectionsPage] Error state:', error);
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  console.log('[AdminConnectionsPage] Rendering with connections:', {
    count: connections.length,
    total: pagination.total
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Connection Management</h1>
        <p className="text-muted-foreground">
          Track and manage all connections between matched parties
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status Filter */}
          <div>
            <label className="text-sm font-semibold mb-2 block">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="connected">Connected</SelectItem>
                <SelectItem value="both_contacted">Both Contacted</SelectItem>
                <SelectItem value="deal_in_progress">Deal In Progress</SelectItem>
                <SelectItem value="deal_completed">Deal Completed</SelectItem>
                <SelectItem value="deal_failed">Deal Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort By */}
          <div>
            <label className="text-sm font-semibold mb-2 block">Sort By</label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
                <SelectItem value="recent-activity">Recent Activity</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results Count */}
          <div className="flex items-end">
            <div className="text-sm">
              <p className="text-muted-foreground">Results</p>
              <p className="text-2xl font-bold">
                {connections.length} / {pagination.total}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connections Table */}
      {connections.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <p className="text-sm text-muted-foreground">
              No connections found.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Landowner</TableHead>
                  <TableHead>Investor</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Connected</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map(connection => (
                  <TableRow key={connection.id}>
                    {/* Landowner */}
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">
                          {connection.landowner?.fullName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {connection.landowner?.email}
                        </p>
                      </div>
                    </TableCell>

                    {/* Investor */}
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">
                          {connection.investor?.fullName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {connection.investor?.email}
                        </p>
                      </div>
                    </TableCell>

                    {/* Score */}
                    <TableCell>
                      <Badge variant="outline">
                        {connection.matchScore}%
                      </Badge>
                    </TableCell>

                    {/* Status with Dropdown */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(connection.status)}>
                          {formatStatus(connection.status)}
                        </Badge>

                        {/* Status Change Dropdown */}
                        <Select
                          value={connection.status}
                          onValueChange={(newStatus) => {
                            if (newStatus !== connection.status) {
                              handleStatusChange(connection.id, newStatus);
                            }
                          }}
                          disabled={updatingId === connection.id}
                        >
                          <SelectTrigger className="w-fit border-none bg-transparent p-0 h-auto">
                            <span className="text-xs text-primary cursor-pointer hover:underline">
                              {updatingId === connection.id ? 'Updating...' : 'Change'}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="connected">Connected</SelectItem>
                            <SelectItem value="both_contacted">Both Contacted</SelectItem>
                            <SelectItem value="deal_in_progress">Deal In Progress</SelectItem>
                            <SelectItem value="deal_completed">Deal Completed</SelectItem>
                            <SelectItem value="deal_failed">Deal Failed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>

                    {/* Connected Date */}
                    <TableCell className="text-sm text-muted-foreground">
                      {connection.createdAt 
                      ? format(new Date(connection.createdAt), 'PPP')
                      : connection.connectedAt
                      ? format(new Date(connection.connectedAt), 'PPP')
                      : 'N/A'
                    }
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewChat(connection.id)}
                        className="gap-2"
                      >
                        <MessageSquare className="w-4 h-4" />
                        Chat
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => i + 1).map(
              page => (
                <Button
                  key={page}
                  variant={currentPage === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              )
            )}
          </div>

          <Button
            variant="outline"
            onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
            disabled={currentPage === pagination.pages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}